import React, { useCallback } from 'react';
import {
  EdgeProps,
  BaseEdge,
  EdgeLabelRenderer, // Import EdgeLabelRenderer
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useStoreApi,
  Edge,
} from 'reactflow';
import { EdgeData, ControlPointData } from '../../types';
import useStore, { FlowState } from '../../store/useStore'; // Import default useStore and FlowState type

import { ControlPoint } from './ControlPoint'; // Use named import


// Selector to get specific store actions/state
const selector = (s: FlowState) => ({
  // updateEdgeData doesn't exist, we'll use storeApi.setState
  takeSnapshot: s.takeSnapshot,
});

const EditableEdge: React.FC<EdgeProps<EdgeData>> = (props) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    data,
    markerEnd,
    markerStart,
    selected,
    // type // Type is part of the edge object, not directly in props
  } = props;

  const store = useStore(selector);
  const storeApi = useStoreApi(); // Get store API to access state directly in callbacks

  // --- Path Calculation ---
  // Determine the correct path based on edge data or type
  const edgeShape = data?.shape || 'default'; // Use shape from data if available
  
  // Initialize variables for path and label position
  let edgePath = '';
  let labelX = 0;
  let labelY = 0;

  // For all edge types, we'll use the same label styling and positioning approach
  // This ensures consistent label rendering across all edge types
  if (edgeShape === 'smoothstep') {
      const [path, x, y] = getSmoothStepPath({ 
        sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition 
      });
      edgePath = path;
      labelX = x;
      labelY = y;
  } else if (edgeShape === 'step') {
      // getStepPath is not directly exported, use smoothstep as fallback or implement step logic
      const [path, x, y] = getSmoothStepPath({ 
        sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 0 
      }); // Simulate step
      edgePath = path;
      labelX = x;
      labelY = y;
  } else if (edgeShape === 'straight') {
      const [path, x, y] = getStraightPath({ 
        sourceX, sourceY, targetX, targetY 
      });
      edgePath = path;
      labelX = x;
      labelY = y;
  } else { // Default to bezier
      const [path, x, y] = getBezierPath({ 
        sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition 
      });
      edgePath = path;
      labelX = x;
      labelY = y;
  }

  // Control points are primarily for Bezier, hide for others for now
  const renderControlPoints = edgeShape === 'default' || edgeShape === 'bezier';
  // TODO: Revisit control point logic if needed for other shapes
  const controlPoints: ControlPointData[] = []; // Placeholder

  // Use the style prop directly provided by React Flow
  // This style object should contain the correct stroke and strokeWidth
  // updated by EdgeConfigRenderer and preserved during loading.
  const edgeStyle = {
    ...style, // Spread the incoming style
    // Ensure defaults if style properties are missing
    stroke: style?.stroke || '#555555', // Default color: dark gray
    strokeWidth: style?.strokeWidth || 2, // Default thickness: 2px
  };

  // Note: We'll use the default ReactFlow selected class (.react-flow__edge.selected)
  // instead of adding our own className to the BaseEdge

  // Callback passed to ControlPoint component.
  // It accepts an updater function, applies it, and updates the store.
  const setControlPointsCallback = useCallback(
    (updater: (prevPoints: ControlPointData[]) => ControlPointData[]) => {
      storeApi.setState((state) => {
        const edge = state.edges.find((e: Edge) => e.id === id);
        if (!edge) {
          // console.warn(`EditableEdge: Edge ${id} not found in store for update.`); // Removed log
          return state; // Return original state if edge not found
        }

        const currentPoints = edge.data?.points || [];
        const newPoints = updater(currentPoints);

        // Create a new edge object with updated data
        const updatedEdge = {
          ...edge,
          data: {
            ...edge.data,
            points: newPoints,
          },
        };

        // Create a new edges array with the updated edge
        const updatedEdges = state.edges.map((e: Edge) =>
          e.id === id ? updatedEdge : e
        );

        // Return the updated state for zustand
        return { ...state, edges: updatedEdges };
      });
    },
    [id, storeApi] // Dependencies for useCallback
  );

  // Callback for when dragging a control point ends
  const handleDragEnd = useCallback(() => {
    store.takeSnapshot();
  }, [store.takeSnapshot]);

  // --- Label Positioning ---
  // Use labelX and labelY returned from the specific path function
  // const [edgePath, labelX, labelY] = pathCalcResult; // Already calculated above

  return (
    <>
      {/* Render the edge path */}
      <BaseEdge
        id={id}
        path={edgePath}
        // Use the marker objects directly - React Flow will handle them
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={edgeStyle}
      />

      {/* Render the edge label using EdgeLabelRenderer - consistent for all edge types */}
      {data?.label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              padding: '2px 4px',
              fontSize: '10px',
              fontWeight: 500,
              textAlign: 'center',
              pointerEvents: 'all',
              color: edgeStyle.stroke,
              WebkitTextStrokeWidth: '4px', // Standard property name in React style objects
              WebkitTextStrokeColor: 'var(--edge-label-shadow-color)', // Use CSS variable
              paintOrder: 'stroke fill', // Ensures stroke is drawn behind the fill
              maxHeight: '76px',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              display: 'block',
              maxWidth: '100px', // Keep maxWidth to encourage wrapping
              lineHeight: '1.2',
              zIndex: 1000,
              backgroundColor: 'transparent'
            }}
            className="nodrag nopan"
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Render control points only when the edge is selected and it's a bezier type */}
      {selected && renderControlPoints && controlPoints.length > 0 && (
        <>
          {controlPoints.map((point, index) => (
            <ControlPoint
              key={point.id}
              id={point.id}
              index={index} // Pass the index within the userControlPoints array
              x={point.x}
              y={point.y}
              active={point.active} // Pass the active status from the point data
              color={edgeStyle.stroke || '#FF0072'} // Use edge stroke color or a default highlight
              setControlPoints={setControlPointsCallback} // Pass the adapted callback
              onDragEnd={handleDragEnd} // Pass snapshot trigger
            />
          ))}
        </>
      )}
    </>
  );
};

export default EditableEdge;
